import { SpeakerIcon } from '../../../../Icons';
import { CardShortProps, CollapsedCard } from './ListCards';
import { AlbumActionsWrapper } from '../../../../Actions/AlbumActions';
import { ArtistActionsWrapper } from '../../../../Actions/ArtistActions';
import { PlayistActionsWrapper } from '../../../../Actions/PlaylistActions';

// Utils
import { useNavigate } from 'react-router-dom';

// Redux
import { useAppDispatch, useAppSelector } from '../../../../../store/store';
import {
  MIX_BUILDER_PLAYLIST_ID,
  MIX_BUILDER_ROUTE,
  yourLibraryActions,
} from '../../../../../store/slices/yourLibrary';

// Services
import { playerService } from '../../../../../services/player';

// Constants
import { ARTISTS_DEFAULT_IMAGE, PLAYLIST_DEFAULT_IMAGE } from '../../../../../constants/spotify';

// Interface
import type { Album } from '../../../../../interfaces/albums';
import type { Artist } from '../../../../../interfaces/artist';
import type { Playlist } from '../../../../../interfaces/playlists';
import type { LibraryRecording } from '../../../../../interfaces/recording';
import type { LibraryWork } from '../../../../../interfaces/work';
import type { LibraryComposer } from '../../../../../interfaces/composer';
import { memo, useCallback } from 'react';
import { getLibraryCollapsed, uiActions } from '../../../../../store/slices/ui';
import { useComposerIdSet } from '../../../../../utils/useComposerIdSet';
import { useComposerImageMap } from '../../../../../utils/useComposerImageMap';
import { getCatalogIdForSpotifyId } from '../../../../../utils/composerRegistry';
import { highlightText } from './highlight';
import { resolveWorkImageUrl } from './workUtils';

const isMixBuilderPlaylist = (playlist?: Playlist | null) =>
  playlist?.id === MIX_BUILDER_PLAYLIST_ID;

const CardCompact = (props: CardShortProps) => {
  const { title, subtitle, isCurrent, onClick, onDoubleClick } = props;

  return (
    <button
      onClick={onClick}
      className='library-card'
      style={{ borderRadius: 10 }}
      onDoubleClick={onDoubleClick}
    >
      <div
        style={{
          width: '100%',
          paddingLeft: 5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', gap: 5, width: '100%', alignItems: 'center' }}>
          <h3
            className='text-md font-semibold text-white'
            style={{
              fontSize: 15,
              fontWeight: 100,
              lineHeight: 2.1,
              maxWidth: subtitle ? '60%' : undefined,
              color: isCurrent ? '#1db954' : undefined,
            }}
          >
            {title}
          </h3>

          <p
            className='text-md font-semibold text-white'
            style={{
              fontSize: 13,
              opacity: 0.7,
              fontWeight: 400,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {subtitle}
          </p>
        </div>

        <div style={{ padding: 8 }}>
          {isCurrent ? <SpeakerIcon fill='#1db954' height={13} width={13} /> : null}
        </div>
      </div>
    </button>
  );
};

const Card = memo((props: CardShortProps) => {
  const collapsed = useAppSelector(getLibraryCollapsed);

  const onDoubleClick = () => {
    if (props.disablePlayback || !props.uri) return;
    playerService.startPlayback({ context_uri: props.uri });
  };

  if (collapsed) {
    return <CollapsedCard {...props} onDoubleClick={onDoubleClick} />;
  }
  return <CardCompact {...props} onDoubleClick={onDoubleClick} />;
});

const ArtistCardShort = memo(({ artist }: { artist: Artist }) => {
  const navigate = useNavigate();
  const filter = useAppSelector((state) => state.yourLibrary.filter);
  const contextUri = useAppSelector((state) => state.spotify.state?.context?.uri);
  const search = useAppSelector((state) => state.yourLibrary.search);
  const composerIds = useComposerIdSet();
  const composer = useAppSelector((state) =>
    state.yourLibrary.myComposers.find((entry) => {
      if (!entry) return false;
      if (entry.spotifyArtistId && entry.spotifyArtistId === artist.id) return true;
      return entry.id === artist.id;
    })
  );

  const isComposer = composerIds.has(artist.id) || !!composer;
  const lifeSpan = composer ? [composer.birthYear, composer.deathYear].filter(Boolean).join('–') : '';
  const extraDetails = [composer?.era, lifeSpan].filter(Boolean).join(' • ');

  let subtitle = '';
  if (filter === 'ALL') {
    if (isComposer) {
      subtitle = extraDetails ? `• Composer • ${extraDetails}` : '• Composer';
    } else {
      subtitle = '• Artist';
    }
  } else if (isComposer && extraDetails) {
    subtitle = `• ${extraDetails}`;
  }

  const imageUrl = composer?.imageUrl ?? artist?.images[0]?.url ?? ARTISTS_DEFAULT_IMAGE;

  const preferredCatalogId =
    composer?.id ??
    getCatalogIdForSpotifyId(composer?.spotifyArtistId ?? artist.id ?? null);
  const destination = isComposer
    ? `/composer/${preferredCatalogId ?? composer?.spotifyArtistId ?? artist.id}`
    : `/artist/${artist.id}`;

  const onClick = useCallback(() => {
    navigate(destination);
  }, [navigate, destination]);

  return (
    <ArtistActionsWrapper artist={artist} trigger={['contextMenu']}>
      <div>
        <Card
          rounded
          uri={artist.uri}
          onClick={onClick}
          title={highlightText(artist.name ?? '', search)}
          isCurrent={contextUri === artist.uri}
          subtitle={highlightText(subtitle, search)}
          image={imageUrl}
        />
      </div>
    </ArtistActionsWrapper>
  );
});

const AlbumCardShort = memo(({ album }: { album: Album }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.auth.user?.id);
  const filter = useAppSelector((state) => state.yourLibrary.filter);
  const contextUri = useAppSelector((state) => state.spotify.state?.context?.uri);
  const search = useAppSelector((state) => state.yourLibrary.search);

  const albumImage = album?.images?.[0]?.url ?? PLAYLIST_DEFAULT_IMAGE;

  const onClick = useCallback(() => {
    if (!userId) {
      return dispatch(uiActions.openLoginModal(albumImage));
    }
    navigate(`/album/${album.id}`);
  }, [userId, navigate, album.id, albumImage, dispatch]);

  return (
    <AlbumActionsWrapper album={album} trigger={['contextMenu']}>
      <div>
        <Card
          uri={album.uri}
          onClick={onClick}
          title={highlightText(album.name ?? '', search)}
          image={albumImage}
          isCurrent={contextUri === album.uri}
          subtitle={highlightText(
            filter === 'ALL' ? `• Album` : `• ${album.artists?.[0]?.name ?? ''}`,
            search
          )}
        />
      </div>
    </AlbumActionsWrapper>
  );
});

const PlaylistCardShort = memo(({ playlist }: { playlist: Playlist }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const filter = useAppSelector((state) => state.yourLibrary.filter);
  const contextUri = useAppSelector((state) => state.spotify.state?.context?.uri);
  const search = useAppSelector((state) => state.yourLibrary.search);

  const isMixBuilder = isMixBuilderPlaylist(playlist);
  const destination = isMixBuilder ? MIX_BUILDER_ROUTE : `/playlist/${playlist.id}`;
  const subtitle = isMixBuilder
    ? '• Build custom mixes'
    : filter === 'ALL'
    ? '• Playlist'
    : '';

  const onClick = useCallback(() => {
    navigate(destination);
  }, [navigate, destination]);

  const card = (
    <Card
      onClick={onClick}
      uri={playlist.uri}
      title={highlightText(playlist.name ?? '', search)}
      isCurrent={isMixBuilder ? false : contextUri === playlist.uri}
      subtitle={highlightText(subtitle, search)}
      disablePlayback={isMixBuilder}
      image={playlist?.images?.length ? playlist?.images[0]?.url : PLAYLIST_DEFAULT_IMAGE}
    />
  );

  if (isMixBuilder) {
    return <div>{card}</div>;
  }

  return (
    <PlayistActionsWrapper
      playlist={playlist}
      trigger={['contextMenu']}
      onRefresh={() => {
        dispatch(yourLibraryActions.fetchMyPlaylists());
      }}
    >
      <div>{card}</div>
    </PlayistActionsWrapper>
  );
});

const WorkCardShort = memo(({ work }: { work: LibraryWork }) => {
  const filter = useAppSelector((state) => state.yourLibrary.filter);
  const search = useAppSelector((state) => state.yourLibrary.search);
  const composerImages = useComposerImageMap();
  const navigate = useNavigate();
  const descriptorParts: string[] = [];

  if (filter === 'ALL') {
    descriptorParts.push('Work');
  } else if (work.composerName) {
    descriptorParts.push(work.composerName);
  }

  const subtitle = descriptorParts.length ? `• ${descriptorParts.join(' • ')}` : '';
  const artwork = resolveWorkImageUrl(work, composerImages) ?? PLAYLIST_DEFAULT_IMAGE;

  return (
    <div>
      <Card
        uri={work.uri}
        title={highlightText(work.name ?? '', search)}
        subtitle={highlightText(subtitle, search)}
        image={artwork}
        disabled
        onClick={() => navigate(`/work/${work.id}`)}
      />
    </div>
  );
});

const RecordingCardShort = memo(({ recording }: { recording: LibraryRecording }) => {
  const filter = useAppSelector((state) => state.yourLibrary.filter);
  const search = useAppSelector((state) => state.yourLibrary.search);
  const navigate = useNavigate();
  const descriptorParts: string[] = [];
  if (filter === 'ALL') {
    descriptorParts.push('Recording');
  } else {
    if (recording.performerName) descriptorParts.push(recording.performerName);
    if (recording.albumName) descriptorParts.push(recording.albumName);
    if (!descriptorParts.length) descriptorParts.push('Recording');
  }

  const subtitle = descriptorParts.length ? `• ${descriptorParts.join(' • ')}` : '';

  return (
    <div>
      <Card
        uri={recording.uri}
        title={highlightText(recording.workName ?? '', search)}
        subtitle={highlightText(subtitle, search)}
        image={recording.imageUrl ?? recording.albumImageUrl ?? PLAYLIST_DEFAULT_IMAGE}
        disabled
        onClick={() => navigate(`/recording/${recording.id}`)}
      />
    </div>
  );
});

const ComposerCardShort = memo(({ composer }: { composer: LibraryComposer }) => {
  const navigate = useNavigate();
  const filter = useAppSelector((state) => state.yourLibrary.filter);
  const search = useAppSelector((state) => state.yourLibrary.search);
  const fallbackImage = useAppSelector((state) => {
    const targetIds = [composer.spotifyArtistId, composer.id].filter(Boolean);
    const match = state.yourLibrary.myArtists.find((entry) => targetIds.includes(entry.id));
    return match?.images?.[0]?.url;
  });

  const lifeSpan = [composer.birthYear, composer.deathYear].filter(Boolean).join('–');
  const extraDetails = [composer.era, lifeSpan].filter(Boolean).join(' • ');

  const subtitle =
    filter === 'ALL'
      ? extraDetails
        ? `• Composer • ${extraDetails}`
        : '• Composer'
      : extraDetails
      ? `• ${extraDetails}`
      : '';

  return (
    <div>
      <Card
        uri={composer.uri}
        title={highlightText(composer.name ?? '', search)}
        subtitle={highlightText(subtitle, search)}
        image={composer.imageUrl ?? fallbackImage ?? ARTISTS_DEFAULT_IMAGE}
        rounded
        disabled
        onClick={() => {
          const targetId =
            composer.id ??
            getCatalogIdForSpotifyId(composer.spotifyArtistId ?? composer.id ?? null) ??
            composer.spotifyArtistId ??
            composer.id;
          navigate(`/composer/${targetId}`);
        }}
      />
    </div>
  );
});

export const CompactItemComponent = ({
  item,
}: {
  item: Artist | Playlist | Album | LibraryWork | LibraryRecording | LibraryComposer;
}) => {
  const key = `${item.type}-${item.id}`;
  if (item.type === 'artist') return <ArtistCardShort key={key} artist={item} />;
  if (item.type === 'album') return <AlbumCardShort key={key} album={item} />;
  if (item.type === 'composer') return <ComposerCardShort key={key} composer={item} />;
  if (item.type === 'work') return <WorkCardShort key={key} work={item} />;
  if (item.type === 'recording') return <RecordingCardShort key={key} recording={item} />;
  return <PlaylistCardShort key={key} playlist={item} />;
};
